import Wrapper, { wrapperTypes } from "global/wrappers/Wrapper";
import { useEffect, useState } from "react";
import { Label } from "semantic-ui-react";
import KeyPadSimple from "./KeyPadSimple";

// ─── Helpers ────────────────────────────────────────────────────────────────

const fmt = (val) => (val === null ? "--" : String(val).padStart(2, "0"));

function getWins(sets) {
  return sets.reduce(
    (acc, s) => {
      if (s.away !== null && s.home !== null) {
        if (s.away > s.home) acc.away++;
        else if (s.home > s.away) acc.home++;
      }
      return acc;
    },
    { away: 0, home: 0 }
  );
}

function visibleSetCount(sets, setsToWin) {

  const wins = getWins(sets)

  const totalWins = wins.away + wins.home

  // minimum visible sets
  let count = setsToWin

  // if match is not complete yet,
  // show one extra set for score entry
  if (
    wins.away < setsToWin &&
    wins.home < setsToWin
  ) {
    count = totalWins + 1
  }

  // never show less than setsToWin
  count = Math.max(count, setsToWin)

  // preserve already-entered sets
  sets.forEach((set, index) => {
    if (
      set?.away !== null ||
      set?.home !== null
    ) {
      count = Math.max(count, index + 1)
    }
  })

  // never exceed available sets
  count = Math.min(count, sets.length)

  return count
}

const _styles = {
  board: {
    width: "100%",
    padding: '1em',
  },
  header: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    gap: 8,
    marginBottom: 14,
  },
  teamLabel: () => ({
    // fontSize: 12,
    textAlign: 'center',
    fontWeight: 'bold'
  }),
  setsWrap: { display: "flex", flexDirection: "column", gap: 6 },
  row: (complete, hasScore) => ({
    display: "grid",
    gridTemplateColumns: "1fr 44px 1fr",
    gap: 8,
    alignItems: "center",
    backgroundColor: complete ? 'var(--sem-green-blackish)' : hasScore ? 'var(--sem-yellow-black)' : '',
    padding: '.25em',
    borderRadius: '8px'
  }),
  scoreCell: (sbc) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 16,
    opacity: sbc ? 1 : .6
  }),
  setBtn: (sbc) => ({
    textAlign: 'center',
    opacity: sbc ? 1 : .6
  }),
  winsRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    gap: 8,
    marginTop: 16,
    padding: 12,
    borderTop: "1px solid #1e1e1e",
  },
  winsCell: { display: "flex", alignItems: "center", justifyContent: "center", gap: 6 },
  pip: (side, filled) => ({
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: filled ? 'var(--sem-blue)' : "grey",
    transition: "all 0.3s",
  }),
  btnUpdate: (disabled) => ({
    height: 44,
    background: disabled ? 'var(--off-black-3)' : 'var(--sem-blue)',
    border: "none",
    borderRadius: 10,
    cursor: disabled ? "not-allowed" : "pointer",
    fontSize: 12,
    color: 'var(--off-white-1)',
    textTransform: "uppercase",
    fontWeight: 500,
    opacity: disabled ? 0.4 : 1,
    transition: "all 0.15s",
    textAlign: 'right',
  }),
};


// ─── Main Component ───────────────────────────────────────────────────────────

export default function MatchScoreboard({
  currentMatch,
  handleUpdate_scores
}) {

  const emptySet = () => ({ away: null, home: null, complete: false });

  const [matchSets, setMatchSets] = useState(Array.from({ length: 5 }, emptySet));
  const [activeSet, setActiveSet] = useState(null);

  console.log('activeSet', activeSet)

  const handleNextSet = () => {
    setActiveSet()
    setActiveSet(activeSet + 1)
  }

  const { home, away, _status } = currentMatch ?? {}
  const { name: homeTeam } = home ?? {}
  const { name: awayTeam } = away ?? {}
  const { setsToWin } = _status ?? {}

  const _setsToWin = setsToWin || 3

  useEffect(() => {
    const { results } = currentMatch ?? {};
    const { scores } = results ?? {};

    const _ms = Array.from({ length: 5 }, () => ({ home: null, away: null, complete: false }));

    if (scores) {
      _.forEach(scores, (score, si) => {
        const index = Number(si) - 1; // align 1-based → 0-based
        if (index >= 0 && index < 5) {
          _ms[index] = {
            home: score.home ?? null,
            away: score.away ?? null,
            complete: validScore(score, si),
          };
        }
      });
    }
    setMatchSets(_ms);
  }, [currentMatch]);

  const wins = getWins(matchSets);
  const shown = visibleSetCount(matchSets, _setsToWin);

  const validScore = (ms, setNumber) => {
    const minScore = setNumber === 5 || setNumber === '5' ? 15 : 25
    if (
      !Number.isInteger(ms.home) ||
      !Number.isInteger(ms.away)
    ) {
      return false
    }

    const high = Math.max(ms.home, ms.away)
    const low = Math.min(ms.home, ms.away)

    return high >= minScore && (high - low) >= 2
  }

  const updateSets = (nxt) => {
    const _scores = {}
    const _matchSets = nxt || matchSets
    _.forEach(_matchSets, (ms, index) => {
      if (ms.home && ms.away) {
        _scores[index + 1] = {
          home: ms.home,
          away: ms.away,
          complete: validScore(ms, index + 1),
        }
      }
    })
    handleUpdate_scores(_scores)
  }

  const handleUpdate = (idx, away, home) => {
    setMatchSets((prev) => {
      const next = [...prev];
      next[idx] = { away, home, complete: validScore({ home, away }, idx) };
      updateSets(next)
      return next;
    });
  };

  const content_board = () => <div style={_styles.board}>
    {/* Team headers */}
    <div style={_styles.header}>
      <div style={_styles.teamLabel()}>{awayTeam}</div>
      <div>vs</div>
      <div style={_styles.teamLabel()}>{homeTeam}</div>
    </div>

    {/* Set rows */}
    <div style={_styles.setsWrap}>
      {Array.from({ length: shown }, (_, i) => {
        const s = matchSets[i];
        let sbc = true;
        if (i > 0) { sbc = matchSets[i - 1]?.complete; }
        const hasScore = s.away !== null && s.home !== null;
        const awayWin = hasScore && s.away > s.home
        const homeWin = hasScore && s.home > s.away
        const complete = s.complete
        return (
          <div key={i} style={_styles.row(complete, hasScore)} onClick={() => sbc && setActiveSet(i)}>
            <Label style={_styles.scoreCell(sbc)} color={awayWin ? 'blue' : 'grey'} size="large">{fmt(s.away)}</Label>
            <Label style={_styles.setBtn(sbc)}>{`S${i + 1}`}</Label>
            <Label style={_styles.scoreCell(sbc)} color={homeWin ? 'blue' : 'grey'} size="large">{fmt(s.home)}</Label>
          </div>
        );
      })}
    </div>

    {/* Win pips */}
    <div style={_styles.winsRow}>
      <div style={_styles.winsCell}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={_styles.pip("away", i < wins.away)} />
        ))}
      </div>
      <Label>{'SETS'}</Label>
      <div style={_styles.winsCell}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={_styles.pip("home", i < wins.home)} />
        ))}
      </div>
    </div>
  </div>


  const wrapper = () => <Wrapper
    content={content_board()}
    wrapperType={wrapperTypes.padded}
  ></Wrapper>

  return (
    <>
      {wrapper()}

      {/* Score entry popup */}
      {activeSet !== null && (
        <KeyPadSimple
          setIndex={activeSet}
          currentMatch={currentMatch}
          matchSet={matchSets[activeSet]}
          onUpdate={handleUpdate}
          onClose={() => setActiveSet(null)}
          onCloseNext={() => handleNextSet()}
        />
      )}
    </>
  );
}
